import styles from './index.module.less';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import { App, Button, Form, Input, Modal, Radio, RadioChangeEvent, Space, Switch, Tooltip } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { useToggle } from 'react-use';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  User,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  updatePassword,
  verifyBeforeUpdateEmail
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Cookies from 'js-cookie';
// import { useConfigProvider } from '@/context/ConfigProvider';
import { isEmailValid } from '@/shared/utils';
import '@/lib/hooks/common/firebaseConfig/init';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { HairPayItemModel, HairRecordsBalanceModel } from '@/types/hairstyle';
import {
  fetchHairstyleEmailNotifyOperateData,
  fetchHairstyleItemListData,
  fetchHairstyleOrderCancelData,
  fetchHairstyleOrderResumeData,
  fetchHairstyleRecordsBalanceData
} from '@/lib/service';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { isProd } from '@/shared/app-common';
import * as React from 'react';

const SettingsDetailContent = () => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const router = useRouter();
  const { onSignOut, onDeleteUser, onCreditsChanged } = useUserProvider();
  const [open, toggle] = useToggle(false);
  const [openDelete, toggleDelete] = useToggle(false);
  const [isEmail, setIsEmail] = useToggle(true);
  const [openUnsubscribe, toggleUnsubscribe] = useState<HairPayItemModel>();
  const [currentBalanceModel, setCurrentBalanceModel] = useState<HairRecordsBalanceModel>(null);
  const { message } = App.useApp();
  const isSignInEmail = Cookies.get('SignInMethod') === 'email';
  const [loading, setLoading] = useState(false);
  const auth = getAuth();
  const [currentUser, setCurrentUser] = useState<User>();
  const [reasonVal, setReasonVal] = useState(1);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [orderList, setOrderList] = useState<HairPayItemModel[]>([]);
  const [isSendEmailLoading, setSendEmailLoading] = useState(false);
  const [lastEmailDate, setLastEmailDate] = useState<any>(null);
  const stripe = useStripe();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        loadSubList();
        loadBanaceList();
      } else {
        router.push('/');
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const loadSubList = () => {
    fetchHairstyleItemListData(router.locale)
      .then((res) => {
        if (res.code === 0) {
          setOrderList(res?.data ?? []);
        } else {
          setOrderList([]);
          message.error(res?.message);
        }
      })
      .catch((error) => {
        setOrderList([]);
        message.error(error?.message);
      });
  };

  const loadBanaceList = () => {
    fetchHairstyleRecordsBalanceData()
      .then((res) => {
        if (res?.code === 0) {
          setCurrentBalanceModel(res?.data);
          onCreditsChanged(res?.data?.balance);
        } else {
          setCurrentBalanceModel(null);
          message.error(res?.message);
        }
      })
      .catch((e) => {
        message.error(e?.message);
      });
  };

  const notifyChange = (isOpen: boolean) => {
    fetchHairstyleEmailNotifyOperateData(isOpen ? 'open' : 'close')
      .then((res) => {
        if (res?.code === 0) {
          setCurrentBalanceModel({
            ...currentBalanceModel,
            email_notify: !currentBalanceModel?.email_notify
          });
        } else {
          message.error(res?.message);
        }
      })
      .catch((e) => {
        message.error(e?.message);
      });
  };

  const onEditClick = (isEmail: boolean) => {
    setIsEmail(isEmail);
    toggle(true);
  };

  const onLogoutClick = (isShowAlert?: boolean) => {
    onSignOut(isShowAlert);
    router.push('/');
  };

  const onDeleteUserClick = () => {
    toggleDelete(true);
  };

  const onDeleteFinish = () => {
    onDeleteUser();
  };

  const onFinish = (values: any) => {
    setLoading(true);
    const accessToken = Cookies.get('accessToken');

    const credential = isSignInEmail
      ? EmailAuthProvider.credential(currentUser?.email, values?.oldPassword)
      : GoogleAuthProvider.credential(null, accessToken);

    reauthenticateWithCredential(currentUser, credential)
      .then(() => {
        if (isEmail) {
          verifyBeforeUpdateEmail(currentUser, values?.newEmail)
            .then(() => {
              message.success(t('TheVerificationEmailHasBeenSentToTheNewEmailAddress_Desc'));
              toggle(false);
              onLogoutClick(false);
              setLoading(false);
            })
            .catch((error) => {
              message.error(error.message);
              setLoading(false);
            });
        } else {
          updatePassword(currentUser, values?.password)
            .then(() => {
              toggle(false);
              message.success(t('Password update successful'));
              setLoading(false);
            })
            .catch(() => {
              message.error(t('Password_Change_Failed_Error_Msg'));
              setLoading(false);
            });
        }
      })
      .catch(() => {
        message.error(t('Password_Change_Failed_Error_Msg'));
        setLoading(false);
      });
  };

  const onUnsubscribeClick = () => {
    setUnsubscribeLoading(true);

    fetchHairstyleOrderCancelData(reasonVal, router.locale)
      .then((res) => {
        if (res.code === 0) {
          message.success(t('UnsubscribeSuccess'));
          // setOrderInfoModel(undefined)
          loadSubList();
        } else {
          message.error(res?.message);
        }
        toggleUnsubscribe(undefined);
        setUnsubscribeLoading(false);
      })
      .catch((error) => {
        message.error(error.message);
        setUnsubscribeLoading(false);
      });
  };

  const loadScripePay = async (client_secret?: any) => {
    if (!client_secret) {
      return;
    }
    await stripe?.redirectToCheckout({
      sessionId: client_secret
    });
  };

  const onResubscribeClick = () => {
    fetchHairstyleOrderResumeData(router.locale)
      .then((res) => {
        if (res.code === 0) {
          if (res?.data?.need_pay?.client_secret && res?.data?.need_pay === true) {
            loadScripePay(res?.data?.need_pay?.client_secret);
            return;
          }
          message.success(t('Re-subscribeSuccess'));
          loadSubList();
        } else {
          message.error(res?.message);
        }
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  const getItem = (
    title: string,
    val: string | React.ReactNode,
    display: string,
    btnName: string | React.ReactNode,
    onClick?: () => void
  ) => {
    return (
      <div className={styles.cardContent}>
        <div>
          <h3>{title}</h3>
          <span>{val}</span>
        </div>
        <Button
          className={styles.editBtn}
          style={{ display: display, padding: typeof btnName === 'string' ? '0 20px' : '0' }}
          onClick={onClick}
        >
          {btnName}
        </Button>
      </div>
    );
  };

  const changeValContent = () => {
    return (
      <Modal className={styles.modalContainer} open={open} onCancel={() => toggle(false)} destroyOnClose={true}>
        <div style={{ padding: '20px' }}>
          <div className={styles.titleContainer}>{isEmail ? t('Change email') : t('Change password')}</div>
          <Form
            form={form}
            name="normal_edit_form"
            layout="vertical"
            className={styles.formContent}
            initialValues={{ oldEmail: currentUser?.email }}
            onFinish={onFinish}
          >
            {isEmail && (
              <Form.Item name="oldEmail" label={t('Current e-mail')} className="no-asterisk">
                <Input
                  className={styles.inputItem}
                  value={currentUser?.email}
                  disabled
                  // placeholder="Current e-mail"
                />
              </Form.Item>
            )}
            {isSignInEmail && (
              <Form.Item
                name="oldPassword"
                rules={[{ required: true, message: t('Please input current password') }]}
                label={t('Current password')}
                className="no-asterisk"
              >
                <Input className={styles.inputItem} type="password" />
              </Form.Item>
            )}
            {isEmail && (
              <Form.Item
                name="newEmail"
                rules={[
                  { required: true, message: t('PleaseInputANewEmail') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('oldEmail') === value || !isEmailValid(value)) {
                        return Promise.reject(new Error(t('PleaseInputANewEmail')));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
                label={t('Enter new e-mail')}
                className="no-asterisk"
              >
                <Input className={styles.inputItem} />
              </Form.Item>
            )}
            {!isEmail && (
              <Form.Item
                name="password"
                rules={[{ required: true, message: t('Please input your new password') }]}
                label={t('New password')}
                className="no-asterisk"
              >
                <Input className={styles.inputItem} type="password" />
              </Form.Item>
            )}
            {!isEmail && (
              <Form.Item
                name="repassword-2"
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('theNewPasswordThatYouEnteredDoNotMatch')));
                    }
                  })
                ]}
                label={t('Confirm new password')}
                className="no-asterisk"
              >
                <Input className={styles.inputItem} type="password" />
              </Form.Item>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" className={styles.bottomButton} loading={loading}>
                {t('Confirm')}
              </Button>
              <Button className={styles.bottomButton} onClick={() => toggle(false)}>
                {t('Cancel')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    );
  };

  const deleteContent = () => {
    return (
      <Modal
        className={styles.modalContainer}
        open={openDelete}
        onCancel={() => toggleDelete(false)}
        destroyOnClose={true}
      >
        <div style={{ padding: '20px' }}>
          <div className={styles.titleContainer} style={{ textAlign: 'center' }}>
            {t('You really want to delete this account')}
          </div>
          <Form
            form={form}
            name="normal_delete_form"
            layout="vertical"
            className={styles.deleteContent}
            onFinish={onDeleteFinish}
          >
            <Form.Item>
              <p className={styles.desc}>{t('AreYouSureYouWantToPermanentlyDeleteYourAccount_Desc')}</p>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" className={styles.bottomButton}>
                {t('Yes_Delete')}
              </Button>
            </Form.Item>
            <Form.Item>
              <Button className={styles.bottomButton} onClick={() => toggleDelete(false)}>
                {t('NotNow_Thanks')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    );
  };

  const unsubscribeContent = () => {
    return (
      <Modal
        className={styles.unsubscribeModalContainer}
        open={openUnsubscribe != undefined}
        onCancel={() => toggleUnsubscribe(undefined)}
        destroyOnClose={true}
        footer={null}
      >
        <div style={{ padding: '20px' }}>
          <div className={styles.titleContainer} style={{ textAlign: 'center' }}>
            {t('ShareYourReasonForLeaving')}
            <p>{t('YourFeedbackMatters_Desc')}</p>
          </div>

          <Radio.Group defaultValue={reasonVal} onChange={(e: RadioChangeEvent) => setReasonVal(e.target.value)}>
            <Space direction="vertical">
              <Radio value={1}>{t('FoundAnAlternativeService')}</Radio>
              <Radio value={2}>{t('FeaturesDontMeetMyNeeds')}</Radio>
              <Radio value={3}>{t('ServiceIsTooComplex')}</Radio>
              <Radio value={4}>{t('TooExpensive')}</Radio>
              <Radio value={5}>{t('ServiceQualityDidntMeetExpectations')}</Radio>
              <Radio value={6}>{t('ConcernsAboutPrivacyOrDataSecurity')}</Radio>
              <Radio value={7}>{t('PersonalReasons')}</Radio>
            </Space>
          </Radio.Group>

          <div className={styles.footerContainer}>
            <Button className={styles.btn} onClick={() => toggleUnsubscribe(undefined)}>
              {t('Cancel')}
            </Button>
            <Button
              className={styles.btn}
              key="submit"
              loading={unsubscribeLoading}
              onClick={() => onUnsubscribeClick()}
            >
              {t('Continue')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const items = (item?: HairPayItemModel) => {
    return (
      <div className={styles.itemListContainer} style={{ color: `var(--app-text-color)` }}>
        <span
          onClick={() => {
            router.push('/billing-records');
          }}
        >
          {'Payment History'}
        </span>
        <span
          onClick={() => {
            if (item?.sub_status === 'normal') {
              toggleUnsubscribe(item);
              ga_logEvent(EventName.settings_Plan_Stop);
            } else if (item?.sub_status === 'cancel') {
              onResubscribeClick();
              ga_logEvent(EventName.settings_Plan_Subscription);
            }
          }}
        >
          {item?.sub_status === 'normal' ? t('StopSubscription') : t('Subscription')}
        </span>
      </div>
    );
  };
  const onSendEmailLink = () => {
    if (isSendEmailLoading) {
      return;
    }

    const now = new Date().getTime();
    if (lastEmailDate && now - lastEmailDate < 60000) {
      // 60000ms = 1 minute
      message.error(t('Avoid frequent requests. Retry in 59s.'));
      return;
    }

    setSendEmailLoading(true);
    setLastEmailDate(now);

    const auth = getAuth();
    sendEmailVerification(auth?.currentUser, { url: process.env.NEXT_APP_WEB_HOST })
      ?.then()
      .then(() => {
        message.success(
          t('A verification email has been sent to x111x. Please check your inbox.').replace(
            'x111x',
            auth?.currentUser?.email
          )
        );
      })
      .catch((error) => {
        message.error(error?.message);
      })
      .finally(() => {
        setSendEmailLoading(false);
      });
  };

  return (
    <CommonBackContent>
      <div className={styles.content}>
        <div className={styles.topContainer}>
          <h1>{t('Settings')}</h1>
        </div>

        {getItem(
          t('Email'),
          currentUser?.email,
          getAuth().currentUser?.emailVerified ? 'none' : 'inline-block',
          t('Send confirm link'),
          onSendEmailLink
        )}

        {getItem(t('Password'), '******', isSignInEmail ? 'inline-block' : 'none', t('Change'), () => {
          onEditClick(false);
          ga_logEvent(EventName.settings_Password_Change);
        })}

        {currentBalanceModel &&
          getItem(
            t('My Credits'),
            <>
              <div>
                <span>{`Your remaining credits: `}</span>
                <span style={{ color: `var(--app-primary-color)` }}>{currentBalanceModel?.balance}</span>
              </div>
            </>,
            'inline-block',
            t('Credits Details'),
            () => {
              router.push(`/my-credits`);
            }
          )}

        {(orderList ?? []).map((item) => {
          if (item?.sub_status != 'normal' && item?.sub_status != 'cancel') {
            return null;
          }
          return getItem(
            t('YourPlan'),
            <>
              <div>{t('YouAreCurrentlyEnrolledInThe_xxx_plan').replace('xxx', `${item?.name}`)}</div>
              {item?.sub_status === 'normal' ? (
                <div>{`${t('YourNextBillingDateIs')} ${item?.expire_time}`} </div>
              ) : (
                <div>{`${t('SubscriptionCancelled_AvailableBefore')} ${item?.expire_time}`}</div>
              )}
            </>,
            'inline-block',
            <Tooltip title={items(item)} color={'#333333'}>
              <div style={{ width: '100%' }}>{`···`}</div>
            </Tooltip>,
            () => {}
          );
        })}

        {currentBalanceModel && (
          <div className={styles.cardContent}>
            <div>
              <h3>{`Email Notification`}</h3>
              <span>{`Receive notifications for friends' feedback on your hairstyle choices.`}</span>
            </div>
            <Switch
              style={{
                background: currentBalanceModel?.email_notify ? `var(--app-primary-color)` : '#999'
              }}
              defaultChecked={currentBalanceModel?.email_notify}
              checked={currentBalanceModel?.email_notify}
              onChange={(e) => {
                notifyChange(e);
              }}
            />
          </div>
        )}

        <div className={styles.bottomContent}>
          <Button
            className={styles.bottomBtn}
            onClick={() => {
              onLogoutClick(true);
              ga_logEvent(EventName.settings_Logout);
            }}
          >
            {t('LogOut')}
          </Button>
          <Button
            className={styles.bottomBtn}
            onClick={() => {
              onDeleteUserClick();
              ga_logEvent(EventName.settings_Delete_Account);
            }}
          >
            {t('Delete Account')}
          </Button>
        </div>
      </div>

      {changeValContent()}
      {deleteContent()}
      {unsubscribeContent()}
    </CommonBackContent>
  );
};

const SettingsContent = () => {
  const stripePromise = loadStripe(
    isProd
      ? 'pk_live_51P8DRNLDPCjDpgYMuO4GPy5uMyd6eOqKUcmifUj2VKevjb5dCopiBCvVDnelb8WVl7suXzRpmhGwavCqwhpXoGEK00YxgWYjnG'
      : 'pk_test_51P8DRNLDPCjDpgYM2JsS8Kz9rCIWkLMnnMDG4nyZHLde4ik1UyqT2EWEaxVOb2z7PyiL3EYAf1pRx668jwYdsgaO00qWVWvZol'
  );

  return (
    <Elements stripe={stripePromise}>
      <SettingsDetailContent />
    </Elements>
  );
};
export default SettingsContent;
